import type { z } from "zod";
import {
  ApiError,
  AuthenticationRequiredError,
  NetworkError,
  ResponseValidationError,
} from "./errors";

/** Supplies the current access token or `null` if the user is not signed in. */
export type AccessTokenProvider = () => Promise<string | null>;

/** Query parameters; `undefined` values are omitted. */
export type QueryParameters = Readonly<Record<string, string | number | undefined>>;

/**
 * Options of the API client.
 */
export interface ApiClientOptions {
  /** Base URL without trailing slash; empty for the same origin. */
  readonly baseUrl: string;
  /** Supplies the bearer token. */
  readonly getAccessToken: AccessTokenProvider;
  /** Fetch implementation (injected for tests). */
  readonly fetch: typeof fetch;
  /** Supplies the tenant chosen by the person, sent as header `X-Tenant-Id` (T-105). */
  readonly getTenantId?: () => Promise<string | null>;
}

/** Options of a single request. */
export interface RequestOptions {
  /** Query parameters. */
  readonly query?: QueryParameters;
  /** Sends the request without an access token (public endpoints). */
  readonly anonymous?: boolean;
  /** Cancels the request. */
  readonly signal?: AbortSignal;
  /** Lets the request outlive the page, for reports sent when the page is hidden (F-68). */
  readonly keepalive?: boolean;
}

/** A downloaded file. */
export interface DownloadedFile {
  /** File content. */
  readonly blob: Blob;
  /** File name from `Content-Disposition`, or `null`. */
  readonly fileName: string | null;
}

/**
 * Thin, typed wrapper around `fetch` for the time tracking API.
 * Every JSON response is validated at runtime with a Zod schema (spec 13.3).
 */
export class ApiClient {
  private readonly options: ApiClientOptions;

  /**
   * Creates the client.
   *
   * @param options - Client options.
   */
  constructor(options: ApiClientOptions) {
    this.options = options;
  }

  /**
   * Sends a GET request and validates the JSON response.
   *
   * @param path - Path starting with `/api/v1`.
   * @param schema - Schema of the response body.
   * @param options - Request options.
   * @returns The validated response body.
   */
  async get<TSchema extends z.ZodType>(
    path: string,
    schema: TSchema,
    options: RequestOptions = {},
  ): Promise<z.infer<TSchema>> {
    const response = await this.send("GET", path, undefined, options);
    const body = await ApiClient.parse(path, response, schema);

    return body;
  }

  /**
   * Sends a POST request with a JSON body and validates the JSON response.
   *
   * @param path - Path starting with `/api/v1`.
   * @param body - Request body, serialized as JSON.
   * @param schema - Schema of the response body.
   * @param options - Request options.
   * @returns The validated response body.
   */
  async post<TSchema extends z.ZodType>(
    path: string,
    body: unknown,
    schema: TSchema,
    options: RequestOptions = {},
  ): Promise<z.infer<TSchema>> {
    const response = await this.send("POST", path, body, options);
    const parsed = await ApiClient.parse(path, response, schema);

    return parsed;
  }

  /**
   * Sends a PUT request with a JSON body and validates the JSON response.
   *
   * @param path - Path starting with `/api/v1`.
   * @param body - Request body, serialized as JSON.
   * @param schema - Schema of the response body.
   * @param options - Request options.
   * @returns The validated response body.
   */
  async put<TSchema extends z.ZodType>(
    path: string,
    body: unknown,
    schema: TSchema,
    options: RequestOptions = {},
  ): Promise<z.infer<TSchema>> {
    const response = await this.send("PUT", path, body, options);
    const parsed = await ApiClient.parse(path, response, schema);

    return parsed;
  }

  /**
   * Sends a DELETE request and validates the JSON response.
   *
   * @param path - Path starting with `/api/v1`.
   * @param schema - Schema of the response body.
   * @param options - Request options.
   * @returns The validated response body.
   */
  async delete<TSchema extends z.ZodType>(
    path: string,
    schema: TSchema,
    options: RequestOptions = {},
  ): Promise<z.infer<TSchema>> {
    const response = await this.send("DELETE", path, undefined, options);
    const parsed = await ApiClient.parse(path, response, schema);

    return parsed;
  }

  /**
   * Downloads a file (exports).
   *
   * @param path - Path starting with `/api/v1`.
   * @param options - Request options.
   * @returns The file content and name.
   */
  async download(path: string, options: RequestOptions = {}): Promise<DownloadedFile> {
    const response = await this.send("GET", path, undefined, options);
    const blob = await response.blob();
    const file: DownloadedFile = {
      blob,
      fileName: parseFileName(response.headers.get("Content-Disposition")),
    };

    return file;
  }

  private async send(
    method: string,
    path: string,
    body: unknown,
    options: RequestOptions,
  ): Promise<Response> {
    const headers = new Headers({ Accept: "application/json" });

    if (options.anonymous !== true) {
      const token = await this.options.getAccessToken();

      if (token === null) {
        throw new AuthenticationRequiredError();
      }

      headers.set("Authorization", `Bearer ${token}`);
      const tenantId = await this.options.getTenantId?.();

      if (tenantId !== undefined && tenantId !== null) {
        headers.set("X-Tenant-Id", tenantId);
      }
    }

    if (body !== undefined) {
      headers.set("Content-Type", "application/json");
    }

    const init: RequestInit = {
      method,
      headers,
      cache: "no-store",
      credentials: "omit",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
      ...(options.keepalive === true ? { keepalive: true } : {}),
    };
    const url = buildUrl(this.options.baseUrl, path, options.query);
    let response: Response;

    try {
      response = await this.options.fetch(url, init);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }

      throw new NetworkError(error);
    }

    if (!response.ok) {
      const apiError = await toApiError(response);
      throw apiError;
    }

    return response;
  }

  private static async parse<TSchema extends z.ZodType>(
    path: string,
    response: Response,
    schema: TSchema,
  ): Promise<z.infer<TSchema>> {
    const json: unknown = await response.json();
    const result = schema.safeParse(json);

    if (!result.success) {
      throw new ResponseValidationError(path, result.error);
    }

    const data: z.infer<TSchema> = result.data;
    return data;
  }
}

/**
 * Builds the request URL from base URL, path and query parameters.
 *
 * @param baseUrl - Base URL without trailing slash; empty for the same origin.
 * @param path - Path with leading slash.
 * @param query - Optional query parameters.
 * @returns The URL.
 */
export function buildUrl(baseUrl: string, path: string, query?: QueryParameters): string {
  const search = new URLSearchParams();

  for (const [name, value] of Object.entries(query ?? {})) {
    if (value !== undefined) {
      search.append(name, String(value));
    }
  }

  const queryString = search.toString();
  const url = `${baseUrl}${path}${queryString === "" ? "" : `?${queryString}`}`;

  return url;
}

/**
 * Extracts the file name from a `Content-Disposition` header.
 *
 * @param header - The header value or `null`.
 * @returns The file name or `null`.
 */
export function parseFileName(header: string | null): string | null {
  if (header === null) {
    return null;
  }

  const extended = /filename\*=UTF-8''([^;]+)/iu.exec(header);

  if (extended?.[1] !== undefined) {
    const decoded = decodeURIComponent(extended[1]);
    return decoded;
  }

  const plain = /filename="?([^";]+)"?/iu.exec(header);
  const fileName = plain?.[1] ?? null;

  return fileName;
}

/**
 * Converts an error response into an {@link ApiError}, reading Problem Details if present.
 *
 * @param response - The failed response.
 * @returns The error.
 */
async function toApiError(response: Response): Promise<ApiError> {
  let code: string | null = null;
  let detail: string | null = null;

  try {
    const body: unknown = await response.json();

    if (typeof body === "object" && body !== null) {
      const record = body as Record<string, unknown>;
      code = typeof record["code"] === "string" ? record["code"] : null;
      detail = typeof record["detail"] === "string" ? record["detail"] : null;
    }
  } catch {
    // The body is not JSON (for example a proxy error page); status alone is reported.
  }

  const error = new ApiError(response.status, code, detail);
  return error;
}
